import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useCompany } from "../context/CompanyContext";
import { useAuth } from "../context/AuthContext";
import { PageHeader, PageBody, Section } from "../components/layout/Page";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Plus, PencilSimple, Trash, DownloadSimple, MagnifyingGlass, Bank, UploadSimple, Eye } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const emptyForm = {
  full_name: "", email: "", phone: "", address: "", department: "", designation: "",
  joining_date: "", salary: 0, work_mode: "Office", status: "active", emergency_contact: "",
  gender: "", dob: "", reporting_manager: "",
};

const emptyBank = {
  bank_name: "", account_holder_name: "", account_number: "", confirm_account_number: "",
  ifsc_code: "", branch_name: "", account_type: "savings", upi_id: "",
};

const WORK_MODES = ["Office", "WFH", "Hybrid"];

export default function Employees() {
  const { user } = useAuth();
  const { activeCompanyId, activeCompany } = useCompany();
  const [items, setItems] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState({ department: "all", designation: "all", status: "all" });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);

  const canEdit = ["super_admin", "hr_admin"].includes(user?.role);
  const canViewBank = ["super_admin", "hr_admin", "employee"].includes(user?.role);
  const canEditBank = ["super_admin", "hr_admin"].includes(user?.role);
  const [bankOpen, setBankOpen] = useState(false);
  const [bankForm, setBankForm] = useState(emptyBank);
  const [bankEmp, setBankEmp] = useState(null);
  const [csvOpen, setCsvOpen] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvResult, setCsvResult] = useState(null);
  const [csvLoading, setCsvLoading] = useState(false);

  const load = async () => {
    if (!activeCompanyId) return;
    const params = { company_id: activeCompanyId, limit: 200 };
    if (search) params.search = search;
    if (filter.department !== "all") params.department = filter.department;
    if (filter.designation !== "all") params.designation = filter.designation;
    if (filter.status !== "all") params.status = filter.status;
    const { data } = await api.get("/employees", { params });
    setItems(data.items);
  };

  const loadMasters = async () => {
    if (!activeCompanyId) return;
    const [d, des, sh] = await Promise.all([
      api.get(`/companies/${activeCompanyId}/departments`),
      api.get(`/companies/${activeCompanyId}/designations`),
      api.get("/shifts", { params: { company_id: activeCompanyId } }),
    ]);
    setDepartments(d.data);
    setDesignations(des.data);
    setShifts(sh.data);
  };

  useEffect(() => { load(); loadMasters(); /* eslint-disable-next-line */ }, [activeCompanyId]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [search, filter]);

  const submit = async () => {
    try {
      const payload = { ...form, salary: parseFloat(form.salary || 0), company_id: activeCompanyId };
      if (editingId) {
        const { company_id, ...rest } = payload;
        await api.put(`/employees/${editingId}`, rest);
        toast.success("Employee updated");
      } else {
        await api.post("/employees", payload);
        toast.success("Employee added");
      }
      setOpen(false); setForm(emptyForm); setEditingId(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const edit = (e) => {
    setForm({
      full_name: e.full_name || "", email: e.email || "", phone: e.phone || "",
      address: e.address || "", department: e.department || "", designation: e.designation || "",
      joining_date: e.joining_date || "", salary: e.salary || 0, work_mode: e.work_mode || "Office",
      status: e.status || "active", emergency_contact: e.emergency_contact || "",
      gender: e.gender || "", dob: e.dob || "", reporting_manager: e.reporting_manager || "",
    });
    setEditingId(e.id);
    setOpen(true);
  };

  const del = async (id) => {
    if (!window.confirm("Delete this employee?")) return;
    await api.delete(`/employees/${id}`);
    toast.success("Deleted");
    load();
  };

  const exportXlsx = async () => {
    const res = await api.get("/reports/export-employees", { params: { company_id: activeCompanyId }, responseType: "blob" });
    const url = window.URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = `employees_${activeCompany?.code}.xlsx`;
    a.click(); window.URL.revokeObjectURL(url);
  };

  const openBank = async (emp) => {
    setBankEmp(emp);
    try {
      const { data } = await api.get(`/employees/${emp.id}/bank`);
      setBankForm({
        bank_name: data.bank_name || "",
        account_holder_name: data.account_holder_name || "",
        account_number: data.account_number || "",
        confirm_account_number: data.account_number || "",
        ifsc_code: data.ifsc_code || "",
        branch_name: data.branch_name || "",
        account_type: data.account_type || "savings",
        upi_id: data.upi_id || "",
      });
    } catch {
      setBankForm(emptyBank);
    }
    setBankOpen(true);
  };

  const saveBank = async () => {
    if (bankForm.account_number !== bankForm.confirm_account_number) {
      toast.error("Account number and confirm account number do not match");
      return;
    }
    try {
      await api.put(`/employees/${bankEmp.id}/bank`, bankForm);
      toast.success("Bank details saved");
      setBankOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save bank details");
    }
  };

  const downloadCsvTemplate = async () => {
    const res = await api.get("/employees/import/template", { responseType: "blob" });
    const url = window.URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = "employees_template.csv"; a.click();
    window.URL.revokeObjectURL(url);
  };

  const submitCsv = async () => {
    if (!csvFile) { toast.error("Choose a CSV file first"); return; }
    setCsvLoading(true);
    setCsvResult(null);
    try {
      const fd = new FormData();
      fd.append("file", csvFile);
      fd.append("company_id", activeCompanyId);
      const { data } = await api.post("/employees/import/csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCsvResult(data);
      if (data.created > 0) {
        toast.success(`${data.created} employee${data.created > 1 ? "s" : ""} imported`);
      }
      if (data.failed_count > 0) {
        toast.warning(`${data.failed_count} row${data.failed_count > 1 ? "s" : ""} skipped — see details`);
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setCsvLoading(false);
    }
  };

  return (
    <div data-testid="employees-page">
      <PageHeader eyebrow={activeCompany?.code || ""} title="Employees" subtitle={`Roster for ${activeCompany?.name || "selected company"}.`}>
        <Button variant="outline" onClick={exportXlsx} className="rounded-sm text-xs uppercase tracking-wider" data-testid="export-employees">
          <DownloadSimple size={14} className="mr-1.5" /> Excel
        </Button>
        {canEdit && (
          <Button variant="outline" onClick={() => { setCsvOpen(true); setCsvResult(null); setCsvFile(null); }} className="rounded-sm text-xs uppercase tracking-wider" data-testid="csv-import-btn">
            <UploadSimple size={14} className="mr-1.5" /> Import CSV
          </Button>
        )}
        {canEdit && (
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(emptyForm); setEditingId(null); } }}>
            <DialogTrigger asChild>
              <Button className="rounded-sm bg-[#2563EB] hover:bg-[#1D4ED8] text-xs uppercase tracking-wider" data-testid="add-employee-btn">
                <Plus size={14} className="mr-1.5" /> New employee
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm max-w-2xl">
              <DialogHeader><DialogTitle>{editingId ? "Edit" : "New"} employee</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3 max-h-[70vh] overflow-y-auto pr-1">
                <div className="col-span-2"><Label className="text-xs uppercase">Full name</Label><Input value={form.full_name} onChange={(e) => setForm({...form, full_name: e.target.value})} className="rounded-sm" data-testid="emp-name" /></div>
                <div><Label className="text-xs uppercase">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="rounded-sm" data-testid="emp-email" /></div>
                <div>
                  <Label className="text-xs uppercase">Phone (10-digit)</Label>
                  <Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="rounded-sm" placeholder="9876543210" data-testid="emp-phone" />
                  <div className="text-[10px] text-[#64748B] mt-1">Indian mobile, starts with 6/7/8/9</div>
                </div>
                <div><Label className="text-xs uppercase">Department</Label>
                  <Select value={form.department} onValueChange={(v) => setForm({...form, department: v})}>
                    <SelectTrigger className="rounded-sm" data-testid="emp-department"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{departments.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs uppercase">Designation</Label>
                  <Select value={form.designation} onValueChange={(v) => setForm({...form, designation: v})}>
                    <SelectTrigger className="rounded-sm" data-testid="emp-designation"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{designations.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs uppercase">Joining date</Label><Input type="date" value={form.joining_date} onChange={(e) => setForm({...form, joining_date: e.target.value})} className="rounded-sm" /></div>
                <div><Label className="text-xs uppercase">Salary (INR)</Label><Input type="number" value={form.salary} onChange={(e) => setForm({...form, salary: e.target.value})} className="rounded-sm" /></div>
                <div><Label className="text-xs uppercase">Work mode</Label>
                  <Select value={form.work_mode} onValueChange={(v) => setForm({...form, work_mode: v})}>
                    <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>{WORK_MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs uppercase">Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({...form, status: v})}>
                    <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                      <SelectItem value="terminated">Terminated</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2"><Label className="text-xs uppercase">Address</Label><Input value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} className="rounded-sm" /></div>
                <div><Label className="text-xs uppercase">Reporting manager</Label><Input value={form.reporting_manager} onChange={(e) => setForm({...form, reporting_manager: e.target.value})} className="rounded-sm" /></div>
                <div><Label className="text-xs uppercase">Emergency contact</Label><Input value={form.emergency_contact} onChange={(e) => setForm({...form, emergency_contact: e.target.value})} className="rounded-sm" /></div>
              </div>
              <DialogFooter><Button onClick={submit} className="rounded-sm bg-[#2563EB] hover:bg-[#1D4ED8]" data-testid="save-employee-btn">Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </PageHeader>

      <PageBody className="space-y-4">
        <div className="flex flex-wrap items-center gap-3 bg-white border border-border p-3 rounded-sm">
          <div className="flex items-center gap-2 border border-border px-2 py-1 rounded-sm flex-1 min-w-[200px]">
            <MagnifyingGlass size={14} className="text-[#64748B]" />
            <input placeholder="Search by name, email, code..." value={search} onChange={(e) => setSearch(e.target.value)} className="bg-transparent text-sm outline-none flex-1" data-testid="employees-search" />
          </div>
          <Select value={filter.department} onValueChange={(v) => setFilter({...filter, department: v})}>
            <SelectTrigger className="w-44 rounded-sm" data-testid="filter-department"><SelectValue placeholder="Department" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All departments</SelectItem>{departments.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filter.status} onValueChange={(v) => setFilter({...filter, status: v})}>
            <SelectTrigger className="w-36 rounded-sm" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All status</SelectItem><SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem><SelectItem value="terminated">Terminated</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Section title={`${items.length} employees`} dense>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Department</TableHead>
                <TableHead>Designation</TableHead><TableHead>Shift</TableHead>
                <TableHead>Work mode</TableHead><TableHead>Status</TableHead>
                {canEdit && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((e) => (
                <TableRow key={e.id} data-testid={`employee-row-${e.employee_code}`}>
                  <TableCell className="font-mono text-xs text-[#2563EB]">{e.employee_code}</TableCell>
                  <TableCell>
                    <div className="font-medium text-[#0F172A]">{e.full_name}</div>
                    <div className="text-xs text-[#64748B]">{e.email}</div>
                  </TableCell>
                  <TableCell className="text-sm">{e.department}</TableCell>
                  <TableCell className="text-sm">{e.designation}</TableCell>
                  <TableCell>
                    {canEdit ? (
                      <Select
                        value={e.shift_id || "__none__"}
                        onValueChange={async (v) => {
                          try {
                            await api.put(`/employees/${e.id}/shift`, { shift_id: v === "__none__" ? null : v });
                            toast.success("Shift updated");
                            load();
                          } catch (err) {
                            toast.error(err.response?.data?.detail || "Failed");
                          }
                        }}
                      >
                        <SelectTrigger className="w-36 rounded-sm h-8 text-xs" data-testid={`shift-${e.employee_code}`}>
                          <SelectValue placeholder="No shift" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">No shift (use default)</SelectItem>
                          {shifts.map((s) => (
                            <SelectItem key={s.id} value={s.id}>{s.name} · {s.start_time}-{s.end_time}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className="text-xs font-mono">{shifts.find((s) => s.id === e.shift_id)?.name || "—"}</span>
                    )}
                  </TableCell>
                  <TableCell><Badge variant="outline" className="rounded-sm font-mono text-[10px]">{e.work_mode}</Badge></TableCell>
                  <TableCell>
                    <span className={`text-[10px] uppercase tracking-wider font-semibold ${e.status === "active" ? "text-[#12B886]" : "text-[#64748B]"}`}>
                      {e.status}
                    </span>
                  </TableCell>
                  {canEdit && (
                    <TableCell className="text-right space-x-1">
                      <Link to={`/employees/${e.id}`} className="inline-block p-1.5 hover:bg-[#EFF6FF] rounded-sm" data-testid={`view-${e.employee_code}`} title="View profile">
                        <Eye size={14} />
                      </Link>
                      {canViewBank && (
                        <button onClick={() => openBank(e)} className="p-1.5 hover:bg-[#EFF6FF] rounded-sm" data-testid={`bank-${e.employee_code}`} title="Bank details">
                          <Bank size={14} />
                        </button>
                      )}
                      <button onClick={() => edit(e)} className="p-1.5 hover:bg-[#EFF6FF] rounded-sm" data-testid={`edit-emp-${e.employee_code}`}><PencilSimple size={14} /></button>
                      <button onClick={() => del(e.id)} className="p-1.5 hover:bg-[#F54236]/10 text-[#F54236] rounded-sm" data-testid={`delete-emp-${e.employee_code}`}><Trash size={14} /></button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {items.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center text-sm text-[#64748B] py-8">No employees found</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Section>
      </PageBody>

      {/* CSV Import Dialog */}
      <Dialog open={csvOpen} onOpenChange={setCsvOpen}>
        <DialogContent className="rounded-sm max-w-2xl" data-testid="csv-dialog">
          <DialogHeader>
            <DialogTitle>Bulk import employees</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-xs text-[#475569] leading-relaxed border border-border bg-[#F1F5F9] p-3 rounded-sm">
              Upload a CSV with these columns: <span className="font-mono text-[#0F172A]">full_name, email, phone, department, designation, joining_date, salary, work_mode</span>.
              <button onClick={downloadCsvTemplate} className="ml-2 text-[#2563EB] underline hover:no-underline" data-testid="csv-template-link">Download template</button>
            </div>
            <div>
              <Label className="text-xs uppercase">CSV File</Label>
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => { setCsvFile(e.target.files?.[0]); setCsvResult(null); }}
                className="block w-full text-xs mt-1 file:mr-3 file:py-1.5 file:px-3 file:rounded-sm file:border-0 file:text-xs file:bg-[#2563EB] file:text-white"
                data-testid="csv-file"
              />
              {csvFile && <div className="text-[10px] text-[#475569] mt-1 font-mono">{csvFile.name} · {(csvFile.size / 1024).toFixed(1)} KB</div>}
            </div>
            {csvResult && (
              <div className="border border-border rounded-sm overflow-hidden" data-testid="csv-result">
                <div className="grid grid-cols-2 divide-x divide-border">
                  <div className="p-3 bg-white">
                    <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#64748B]">Created</div>
                    <div className="font-display text-2xl text-[#12B886] mt-1" data-testid="csv-created-count">{csvResult.created}</div>
                  </div>
                  <div className="p-3 bg-white">
                    <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#64748B]">Failed</div>
                    <div className={`font-display text-2xl mt-1 ${csvResult.failed_count > 0 ? "text-[#F54236]" : "text-[#0F172A]"}`} data-testid="csv-failed-count">{csvResult.failed_count}</div>
                  </div>
                </div>
                {csvResult.failed?.length > 0 && (
                  <div className="border-t border-border max-h-44 overflow-y-auto scrollbar-thin">
                    <table className="w-full text-xs">
                      <thead className="bg-[#F1F5F9] text-[10px] uppercase tracking-wider text-[#64748B]">
                        <tr><th className="text-left px-3 py-1.5">Row</th><th className="text-left px-3 py-1.5">Name</th><th className="text-left px-3 py-1.5">Email</th><th className="text-left px-3 py-1.5">Error</th></tr>
                      </thead>
                      <tbody>
                        {csvResult.failed.map((f, i) => (
                          <tr key={i} className="border-t border-border" data-testid={`csv-fail-row-${i}`}>
                            <td className="px-3 py-1.5 font-mono">{f.row}</td>
                            <td className="px-3 py-1.5">{f.name}</td>
                            <td className="px-3 py-1.5">{f.email}</td>
                            <td className="px-3 py-1.5 text-[#F54236]">{f.error}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCsvOpen(false)} className="rounded-sm text-xs uppercase tracking-wider">Close</Button>
            <Button onClick={submitCsv} disabled={!csvFile || csvLoading} className="rounded-sm bg-[#2563EB] hover:bg-[#1D4ED8] text-xs uppercase tracking-wider" data-testid="csv-submit">
              {csvLoading ? "Importing…" : "Import"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bank Details Dialog */}
      <Dialog open={bankOpen} onOpenChange={setBankOpen}>
        <DialogContent className="rounded-sm max-w-xl" data-testid="bank-dialog">
          <DialogHeader>
            <DialogTitle>Bank Details — {bankEmp?.full_name}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-xs uppercase">Bank Name</Label>
              <Input value={bankForm.bank_name} onChange={(e) => setBankForm({...bankForm, bank_name: e.target.value})} className="rounded-sm" disabled={!canEditBank} data-testid="bank-name" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase">Account Holder Name</Label>
              <Input value={bankForm.account_holder_name} onChange={(e) => setBankForm({...bankForm, account_holder_name: e.target.value})} className="rounded-sm" disabled={!canEditBank} data-testid="bank-holder" />
            </div>
            <div>
              <Label className="text-xs uppercase">Account Number</Label>
              <Input value={bankForm.account_number} onChange={(e) => setBankForm({...bankForm, account_number: e.target.value.replace(/\D/g, "")})} className="rounded-sm font-mono" disabled={!canEditBank} data-testid="bank-acc" />
            </div>
            <div>
              <Label className="text-xs uppercase">Confirm Account No.</Label>
              <Input value={bankForm.confirm_account_number} onChange={(e) => setBankForm({...bankForm, confirm_account_number: e.target.value.replace(/\D/g, "")})} className="rounded-sm font-mono" disabled={!canEditBank} data-testid="bank-acc-confirm" />
              {bankForm.account_number && bankForm.confirm_account_number && bankForm.account_number !== bankForm.confirm_account_number && (
                <div className="text-[10px] text-[#F54236] mt-1">Account numbers do not match</div>
              )}
            </div>
            <div>
              <Label className="text-xs uppercase">IFSC Code</Label>
              <Input value={bankForm.ifsc_code} onChange={(e) => setBankForm({...bankForm, ifsc_code: e.target.value.toUpperCase()})} className="rounded-sm font-mono" maxLength={11} disabled={!canEditBank} data-testid="bank-ifsc" />
              <div className="text-[10px] text-[#64748B] mt-1">Format: AAAA0XXXXXX</div>
            </div>
            <div>
              <Label className="text-xs uppercase">Branch Name</Label>
              <Input value={bankForm.branch_name} onChange={(e) => setBankForm({...bankForm, branch_name: e.target.value})} className="rounded-sm" disabled={!canEditBank} />
            </div>
            <div>
              <Label className="text-xs uppercase">Account Type</Label>
              <Select value={bankForm.account_type} onValueChange={(v) => setBankForm({...bankForm, account_type: v})} disabled={!canEditBank}>
                <SelectTrigger className="rounded-sm" data-testid="bank-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="savings">Savings</SelectItem>
                  <SelectItem value="current">Current</SelectItem>
                  <SelectItem value="salary">Salary</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase">UPI ID (optional)</Label>
              <Input value={bankForm.upi_id} onChange={(e) => setBankForm({...bankForm, upi_id: e.target.value})} className="rounded-sm" disabled={!canEditBank} placeholder="name@bank" />
            </div>
          </div>
          {canEditBank ? (
            <DialogFooter>
              <Button onClick={saveBank} className="rounded-sm bg-[#2563EB] hover:bg-[#1D4ED8]" data-testid="bank-save">Save bank details</Button>
            </DialogFooter>
          ) : (
            <div className="text-xs text-[#64748B] pt-3 border-t border-border">Read-only — only Super Admin / HR Admin can edit bank details.</div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
